/**
 * @file api.js
 * @description Configura una instancia de Axios con un interceptor para manejar la renovación automática de tokens.
 */
import axios from 'axios';

const api = axios.create({
    baseURL: (process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api',
    headers: {
        'Content-Type': 'application/json'
    }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

export const setupInterceptors = (authContext) => {
    const requestInterceptor = api.interceptors.request.use(
        config => {
            try {
                const tokens = localStorage.getItem('authTokens');
                if (tokens) {
                    const parsedTokens = JSON.parse(tokens);
                    if (parsedTokens && parsedTokens.token) {
                        config.headers['Authorization'] = `Bearer ${parsedTokens.token}`;
                    }
                }
            } catch (e) {
                console.error("Could not parse auth tokens from localStorage", e);
            }
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    const responseInterceptor = api.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;
            const { logoutUser, updateAuthAndUser } = authContext;

            // Don't try to refresh the token if the refresh request itself failed
            // This prevents infinite loops
            const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');

            if (error.response && error.response.status === 401 && !originalRequest._retry && !isRefreshRequest) {

                const storedTokens = localStorage.getItem('authTokens');
                const authTokens = storedTokens ? JSON.parse(storedTokens) : null;

                if (isRefreshing) {
                    return new Promise(function (resolve, reject) {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return api(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                if (!authTokens || !authTokens.refreshToken) {
                    console.warn('No refresh token available, logging out');
                    isRefreshing = false;
                    if (logoutUser) logoutUser();
                    return Promise.reject(error);
                }

                try {
                    console.log('Token expired, attempting refresh...');

                    // Use axios directly to avoid interceptor loop
                    const response = await axios.post(
                        `${process.env.REACT_APP_API_URL}/api/auth/refresh`,
                        { refreshToken: authTokens.refreshToken },
                        { headers: { 'Content-Type': 'application/json' } }
                    );

                    const newAuthTokens = {
                        token: response.data.token,
                        // SEC-10b: /refresh ROTA el refresh token. Hay que guardar el nuevo que
                        // devuelve el backend; seguir usando el viejo (ya revocado) dispararía la
                        // detección de reutilización y cerraría la sesión.
                        refreshToken: response.data.refreshToken || authTokens.refreshToken
                    };

                    console.log('Token refreshed successfully');

                    // Update localStorage FIRST
                    localStorage.setItem('authTokens', JSON.stringify(newAuthTokens));

                    // Update API default headers
                    api.defaults.headers.common['Authorization'] = `Bearer ${newAuthTokens.token}`;

                    // Update auth context
                    if (updateAuthAndUser) {
                        updateAuthAndUser(newAuthTokens);
                    }

                    // Update the original request header
                    originalRequest.headers['Authorization'] = `Bearer ${newAuthTokens.token}`;

                    // Process any queued requests
                    processQueue(null, newAuthTokens.token);

                    // Retry the original request
                    return api(originalRequest);

                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError.response?.status, refreshError.message);
                    processQueue(refreshError, null);
                    if (logoutUser) logoutUser();
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            return Promise.reject(error);
        }
    );

    return { requestInterceptor, responseInterceptor };
};

export const getSettings = () => api.get('/settings');

/**
 * @function updateSettings
 * @description Sends a PUT request to update the business settings.
 * @param {Object} settingsData - The new settings data.
 * @returns {Promise} A promise that resolves with the server response.
 */
export const updateSettings = (settingsData) => api.put('/settings', settingsData);

/**
 * @function checkSlugAvailability
 * @description Checks if a slug is available for use.
 * @param {string} slug - The slug to check.
 * @returns {Promise} A promise that resolves with availability status.
 */
export const checkSlugAvailability = (slug) => api.get('/settings/check-slug', { params: { slug } });

// --- Product CRUD --- //
export const getProducts = (params) => api.get('/products', { params });
export const createProduct = (productData) => api.post('/products', productData);
export const updateProduct = (productId, productData) => api.put(`/products/${productId}`, productData);
export const deleteProduct = (productId) => api.delete(`/products/${productId}`);
export const bulkCreateProducts = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

// --- AI Product Suggestions (task 017) --- //
export const startProductSuggestionAnalysis = (reanalyze = false) => api.post('/product-suggestions/analyze', { reanalyze });
// `source` scopes the poll to one flow (task 017 follow-up, 2026-09-01) — defaults to the
// sales-history flow server-side when omitted, so existing callers are unaffected.
export const getSuggestionJobStatus = (source) => api.get('/product-suggestions/jobs/latest', { params: source ? { source } : undefined });
export const getProductSuggestions = (params) => api.get('/product-suggestions', { params });
export const acceptProductSuggestion = (suggestionId) => api.post(`/product-suggestions/${suggestionId}/accept`);
export const rejectProductSuggestion = (suggestionId) => api.post(`/product-suggestions/${suggestionId}/reject`);
// `source` scopes acceptance to one flow (task 017 follow-up, 2026-09-01) — without it, both
// flows' pending suggestions would be accepted together.
export const acceptAllProductSuggestions = (source) => api.post('/product-suggestions/accept-all', { source });
export const getAiBudget = () => api.get('/product-suggestions/budget');
// Generar productos con IA a partir de la descripción del negocio (task 017 follow-up, 2026-09-01).
export const generateProductsFromDescription = (description, count) => api.post('/product-suggestions/generate-from-description', { description, count });

// --- AI Settings (Admin, task 017) --- //
export const getAiSettings = () => api.get('/admin/ai-settings');
export const updateAiSettings = (settings) => api.put('/admin/ai-settings', settings);
export const getAiPricing = () => api.get('/admin/ai-pricing');
export const updateAiPricing = (model, pricing) => api.put(`/admin/ai-pricing/${model}`, pricing);
export const getAiAnalyses = (params) => api.get('/admin/ai-analyses', { params });
export const getAiAnalysesSummary = (params) => api.get('/admin/ai-analyses/summary', { params });
export const getAiBudgetSettings = () => api.get('/admin/ai-budget-settings');
export const updateAiBudgetSettings = (settings) => api.put('/admin/ai-budget-settings', settings);
export const updateExchangeRate = (currency, rate_to_usd) => api.put(`/admin/ai-budget-settings/exchange-rates/${currency}`, { rate_to_usd });

// --- Sales CRUD --- //
export const getSales = (params) => api.get('/sales', { params });
export const getSaleById = (id) => api.get(`/sales/${id}`);

// --- Client CRUD --- //
export const getClients = (params) => api.get('/clients', { params });
export const createClient = (clientData) => api.post('/clients', clientData);
export const updateClient = (clientId, clientData) => api.put(`/clients/${clientId}`, clientData);
export const deleteClient = (clientId) => api.delete(`/clients/${clientId}`);

// --- Suppliers CRUD --- //
export const getSuppliers = (params) => api.get('/suppliers', { params });
export const createSupplier = (supplierData) => api.post('/suppliers', supplierData);
export const updateSupplier = (supplierId, supplierData) => api.put(`/suppliers/${supplierId}`, supplierData);
export const deleteSupplier = (supplierId) => api.delete(`/suppliers/${supplierId}`);

// --- Expenses CRUD --- //
export const getExpenses = (params) => api.get('/expenses', { params });
export const createExpense = (expenseData) => api.post('/expenses', expenseData);
export const updateExpense = (expenseId, expenseData) => api.put(`/expenses/${expenseId}`, expenseData);
export const deleteExpense = (expenseId) => api.delete(`/expenses/${expenseId}`);
export const getPublicExpense = (publicId) => api.get(`/expenses/public/${publicId}`);

// --- Expense Categories (personalizadas por negocio) --- //
export const getExpenseCategories = (params) => api.get('/expense-categories', { params });
export const createExpenseCategory = (data) => api.post('/expense-categories', data);
export const updateExpenseCategory = (id, data) => api.put(`/expense-categories/${id}`, data);
export const deactivateExpenseCategory = (id) => api.delete(`/expense-categories/${id}`);
export const activateExpenseCategory = (id) => api.patch(`/expense-categories/${id}/activate`);

// --- Incomes CRUD (otros ingresos: dinero que entra y no es una venta) --- //
export const getIncomes = (params) => api.get('/incomes', { params });
export const createIncome = (incomeData) => api.post('/incomes', incomeData);
export const updateIncome = (incomeId, incomeData) => api.put(`/incomes/${incomeId}`, incomeData);
export const deleteIncome = (incomeId) => api.delete(`/incomes/${incomeId}`);

// --- Production board (tarea 031: pedidos del día para producir) --- //
// Se consulta en polling: timeout propio para que una request colgada no frene el ciclo.
export const getProductionBoard = (params, config = {}) => api.get('/production-board', { params, timeout: 20000, ...config });
export const setOrderPrepared = (saleId, prepared) => api.patch(`/production-board/${saleId}/prepared`, { prepared });

/**
 * @function uploadLogo
 * @description Uploads a logo file to the server.
 * @param {File} file - The logo file to upload.
 * @returns {Promise} A promise that resolves with the server response containing the logo URL.
 */
export const uploadLogo = (file) => {
    const formData = new FormData();
    formData.append('logo', file);

    return api.post('/settings/upload-logo', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const uploadQrCode = (file) => {
    const formData = new FormData();
    formData.append('qr_code', file);

    return api.post('/settings/upload-qr', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const changePassword = (passwords) => api.post('/auth/change-password', passwords);

// --- Public Client Self-Registration --- //
export const getBusinessDetailsPublic = (businessSlug) => api.get(`/public/businesses/${businessSlug}`);
export const registerClientPublic = (businessSlug, clientData) => api.post(`/public/businesses/${businessSlug}/register-client`, clientData);

// --- Public Catalog --- //
export const getPublicCatalog = (businessSlug) => api.get(`/public/businesses/${businessSlug}/catalog`);

// --- Product Image Upload --- //
export const uploadProductImage = (file) => {
    const formData = new FormData();
    formData.append('product_image', file);
    return api.post('/products/upload-image', formData, {
        headers: { 'Content-Type': undefined },
    });
};

export const getPlansPublic = () => api.get('/plans');
export const getFeatureDefinitions = () => api.get('/plans/features');

// --- Business Management --- //
export const getMyBusinesses = () => api.get('/businesses/mine');
export const createBusiness = (businessData) => api.post('/businesses', businessData);
export const switchBusiness = (targetBusinessId) => api.post('/auth/switch-business', { target_business_id: targetBusinessId });

// --- Admin Plans --- //
export const getAdminPlans = () => api.get('/admin/plans');
export const createPlan = (planData) => api.post('/admin/plans', planData);
export const updatePlan = (planId, planData) => api.put(`/admin/plans/${planId}`, planData);
export const togglePlanStatus = (planId, isActive) => api.put(`/admin/plans/${planId}/status`, { is_active: isActive });

// --- Admin Impersonation Audit (SEC-12) --- //
export const getImpersonationLogs = (params) => api.get('/admin/impersonation-logs', { params });

// --- Contact Form --- //
export const submitContactMessage = (data) => api.post('/public/contact', data);
export const getContactMessages = () => api.get('/admin/contact-messages');

// --- Supplier Settlements --- //
export const getSettlements = (params) => api.get('/settlements', { params });
export const getSettlement = (settlementId) => api.get(`/settlements/${settlementId}`);
export const getDeductibleExpenses = (supplierId) => api.get(`/settlements/deductible/${supplierId}`);
export const createSettlement = (settlementData) => api.post('/settlements', settlementData);
export const updateSettlement = (settlementId, settlementData) => api.put(`/settlements/${settlementId}`, settlementData);
export const deleteSettlement = (settlementId) => api.delete(`/settlements/${settlementId}`);
export const getPublicSettlement = (publicId) => api.get(`/public/settlements/${publicId}`);

// --- Cash Register --- //
export const getCurrentCashRegister = (params) => api.get('/cash-register/current', { params });
export const getCashRegisterSummary = () => api.get('/cash-register/summary');
export const openCashRegister = (data) => api.post('/cash-register/open', data);
export const closeCashRegister = (data) => api.post('/cash-register/close', data);
export const getCashRegisterHistory = (params) => api.get('/cash-register/history', { params });
export const getCashRegisterDetails = (id) => api.get(`/cash-register/${id}`);
export const getAllOpenRegisters = () => api.get('/cash-register/all-open');
export const forceCloseCashRegister = (id, data) => api.post(`/cash-register/${id}/force-close`, data);
export const getMyAssignedStations = () => api.get('/cash-register/my-stations');
export const getOpenRegistersContext = () => api.get('/cash-register/open-context');

// --- Sales --- //
export const checkoutSale = (saleId, paymentData) => api.post(`/sales/${saleId}/checkout`, paymentData);

// --- Tables (Mesas) --- //
export const getTables = (params) => api.get('/tables', { params });
export const createTable = (data) => api.post('/tables', data);
export const bulkCreateTables = (data) => api.post('/tables/bulk', data);
export const updateTable = (id, data) => api.put(`/tables/${id}`, data);
export const deactivateTable = (id) => api.delete(`/tables/${id}`);
export const activateTable = (id) => api.patch(`/tables/${id}/activate`);

// --- Cash Register Stations --- //
export const getCashRegisterStations = (params) => api.get('/cash-register-stations', { params });
export const createCashRegisterStation = (data) => api.post('/cash-register-stations', data);
export const updateCashRegisterStation = (id, data) => api.put(`/cash-register-stations/${id}`, data);
export const deleteCashRegisterStation = (id) => api.delete(`/cash-register-stations/${id}`);
export const getStationResponsibles = (stationId) => api.get(`/cash-register-stations/${stationId}/responsibles`);
export const assignStationResponsible = (stationId, userId) => api.post(`/cash-register-stations/${stationId}/responsibles`, { user_id: userId });
export const removeStationResponsible = (stationId, userId) => api.delete(`/cash-register-stations/${stationId}/responsibles/${userId}`);

// --- Role Permissions --- //
export const getAllRolePermissions = () => api.get('/role-permissions');
export const getRolePermissions = (role) => api.get(`/role-permissions/${role}`);
export const updateRolePermissions = (role, permissions) => api.put(`/role-permissions/${role}`, { permissions });
export const resetRolePermissions = (role) => api.delete(`/role-permissions/${role}`);

export default api;



